import { useAuthStore } from '@clarix/auth-store';
import { useAppModuleEnabled } from '@clarix/rbac';
import { useMe } from '@/core/api';
import { APP_REGISTRY } from '@/gateway/apps';
import { isAppLaunchable } from '@/gateway/appAvailability';
import { CALENDAR_SHELL_PERM } from '@/gateway/calendar-permissions';
import { useActiveWorkspaceSlug } from '@/gateway/getWorkspaceSlug';
import { useAppRailPermissions } from '@/gateway/sidebar/rail-permissions';

const calendarApp = APP_REGISTRY.find((a) => a.id === 'calendar');
/** Calendar's App modules the shell reads; mirror CALENDAR_MODULE in the remote. */
const RECORDINGS_MODULE = 'recordings';
const ROOMS_MODULE = 'rooms';

export interface CalendarShellAccess {
  userId: string | null;
  slug: string | null;
  /** CALENDAR is launchable and its permission map has answered. Fails closed. */
  ready: boolean;
  canViewMeetings: boolean;
  /** `recordings.create` and the recordings module: record, save an unfinished recording, and spend AI. */
  canRecord: boolean;
  /** The top bar's "Schedule meeting". */
  canScheduleMeetings: boolean;
  /** The top bar's "Book a room": `rooms.book` and the rooms module. */
  canBookRooms: boolean;
}

/**
 * The shell's always-on Calendar gate: the CALENDAR entitlement from `/me`, then the rail's own
 * permission query (same key, so it shares the rail's cache whichever app is open). Never
 * `usePermissionsSlice().loadedApps.calendar`, which is false until the remote has mounted.
 */
export function useCalendarShellAccess(): CalendarShellAccess {
  const slug = useActiveWorkspaceSlug();
  const userId = useAuthStore((s) => (s.user?.sub != null ? String(s.user.sub) : null));
  const me = useMe({ activeSlug: slug ?? undefined });
  const launchable = isAppLaunchable(me.data?.active_workspace_apps?.CALENDAR);
  const rail = useAppRailPermissions(launchable ? calendarApp : undefined);
  const ready = launchable && !!calendarApp && rail.status === 'ready';
  // Outside the plan the mic, pill and start form hide; the caller's own rows still show.
  const recordingsOn = useAppModuleEnabled('calendar', RECORDINGS_MODULE);
  const roomsOn = useAppModuleEnabled('calendar', ROOMS_MODULE);
  return {
    userId,
    slug,
    ready,
    canViewMeetings: ready && rail.permissions[CALENDAR_SHELL_PERM.MEETINGS_VIEW] === true,
    canRecord: ready && recordingsOn && rail.permissions[CALENDAR_SHELL_PERM.RECORDINGS_CREATE] === true,
    canScheduleMeetings: ready && rail.permissions[CALENDAR_SHELL_PERM.MEETINGS_CREATE] === true,
    canBookRooms: ready && roomsOn && rail.permissions[CALENDAR_SHELL_PERM.ROOMS_BOOK] === true,
  };
}
